import React, { useState, useEffect, useCallback } from 'react';
import { Clock, User, Phone, Calendar, CheckCircle, XCircle, Loader2, ListOrdered, ArrowUpCircle } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { waitlistAPI } from '../services/api';
import { format, parseISO } from 'date-fns';
import { Button, EmptyState, InfoBanner, StatusPill, cn } from '../components/ui';
import { composeSlotLabel } from '../utils/slotName';

// Map waitlist domain statuses onto the design-system StatusPill keys.
const STATUS_PILL = {
  waiting: { status: 'waitlist', label: 'Waiting' },
  promoted: { status: 'confirmed', label: 'Promoted' },
  expired: { status: 'neutral', label: 'Expired' },
  cancelled: { status: 'cancelled', label: 'Cancelled' },
};

const FILTER_TABS = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'promoted', label: 'Promoted' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const Waitlist = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('waiting');
  const [promotingId, setPromotingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [promoteModal, setPromoteModal] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      const res = await waitlistAPI.getAll(filters);
      if (res.success) setEntries(res.data || []);
      else setError(res.message || 'Failed to load waitlist');
    } catch (err) {
      setError(err.message || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handlePromote = async (id) => {
    const entry = entries.find(e => e.id === id);
    setPromoteModal({ ...entry, total_amount: '', advance_amount: '', payment_mode: 'cash' });
  };

  const confirmPromote = async () => {
    if (!promoteModal) return;
    setPromotingId(promoteModal.id);
    try {
      const res = await waitlistAPI.promote(promoteModal.id, {
        total_amount: Number(promoteModal.total_amount) || 0,
        advance_amount: Number(promoteModal.advance_amount) || 0,
        payment_mode: promoteModal.payment_mode || 'cash',
      });
      if (res.success) {
        setPromoteModal(null);
        fetchEntries();
      } else {
        setError(res.message || 'Failed to promote');
      }
    } catch (err) {
      setError(err.message || 'Failed to promote');
    } finally {
      setPromotingId(null);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this waitlist entry?')) return;
    setCancellingId(id);
    try {
      const res = await waitlistAPI.cancel(id);
      if (res.success) fetchEntries();
      else setError(res.message || 'Failed to cancel');
    } catch (err) {
      setError(err.message || 'Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  const fmtDate = (d) => {
    try {
      const parsed = typeof d === 'string' ? parseISO(d) : new Date(d);
      return format(parsed, 'MMM dd, yyyy');
    } catch { return d || 'N/A'; }
  };

  const waitingCount = entries.filter(e => e.status === 'waiting').length;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-ga-text-primary flex items-center gap-2">
              <ListOrdered className="h-7 w-7 text-ga-primary" />
              Waitlist
            </h1>
            <p className="text-sm text-ga-text-muted mt-1">
              {waitingCount} {waitingCount === 1 ? 'entry' : 'entries'} waiting for a slot
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={cn(
                  'rounded-ga-full border px-4 py-1.5 text-sm font-semibold transition-colors',
                  statusFilter === tab.value
                    ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                    : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-text-primary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <InfoBanner variant="danger">{error}</InfoBanner>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 text-ga-primary animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            card
            icon={Clock}
            title="No Waitlist Entries"
            description={statusFilter === 'waiting' ? 'No one is currently waiting.' : `No ${statusFilter} entries found.`}
          />
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => {
              const pill = STATUS_PILL[entry.status] || { status: 'neutral', label: entry.status };
              const slotLabel = composeSlotLabel(entry.slot_name || entry.cs_slot_name, entry.time_slot);
              return (
                <div
                  key={entry.id}
                  className={cn(
                    'relative overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface p-5 shadow-ga-soft',
                    'before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-ga-primary before:content-[""]'
                  )}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Left: details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base font-semibold text-ga-text-primary">
                          {entry.service_name || 'Service'}
                        </h3>
                        <span className="text-xs text-ga-text-muted">#{entry.id}</span>
                        <StatusPill status={pill.status} label={pill.label} size="sm" />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-ga-text-muted">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 shrink-0" />
                          <span>{fmtDate(entry.date)}</span>
                          {slotLabel !== 'N/A' && (
                            <span className="text-xs">· {slotLabel}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span>{entry.contact_name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          {entry.contact_phone ? (
                            <a href={`tel:${entry.contact_phone}`} className="text-ga-primary hover:underline">
                              {entry.contact_phone}
                            </a>
                          ) : (
                            <span>—</span>
                          )}
                        </div>
                      </div>

                      {(entry.team_a_name || entry.team_b_name) && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {entry.team_a_name && (
                            <span className="text-xs bg-ga-info/15 text-ga-info px-2 py-0.5 rounded-ga-sm">
                              Team A: {entry.team_a_name}
                            </span>
                          )}
                          {entry.team_b_name && (
                            <span className="text-xs bg-ga-success/15 text-ga-success px-2 py-0.5 rounded-ga-sm">
                              Team B: {entry.team_b_name}
                            </span>
                          )}
                        </div>
                      )}

                      {entry.notes && (
                        <p className="text-xs text-ga-text-subtle mt-2 italic">{entry.notes}</p>
                      )}

                      <p className="text-xs text-ga-text-subtle mt-2">
                        Added {fmtDate(entry.created_at)}
                      </p>
                    </div>

                    {/* Right: actions */}
                    {entry.status === 'waiting' && (
                      <div className="flex flex-row lg:flex-col gap-2 shrink-0">
                        <Button
                          variant="primary"
                          onClick={() => handlePromote(entry.id)}
                          disabled={promotingId === entry.id}
                          className="gap-1.5 text-sm"
                        >
                          <ArrowUpCircle className="h-4 w-4" />
                          Promote to Booking
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleCancel(entry.id)}
                          disabled={cancellingId === entry.id}
                          className="gap-1.5 text-sm border-ga-danger/40 text-ga-danger hover:bg-ga-danger/10"
                        >
                          <XCircle className="h-4 w-4" />
                          Cancel
                        </Button>
                      </div>
                    )}

                    {entry.status === 'promoted' && entry.promoted_booking_id && (
                      <div className="flex items-center gap-1.5 text-sm text-ga-success">
                        <CheckCircle className="h-4 w-4" />
                        Booking #{entry.promoted_booking_id}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Promote Modal */}
      {promoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPromoteModal(null)} />
          <div className="relative bg-ga-bg-surface border border-ga-border-subtle rounded-ga-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-ga-text-primary mb-4">Promote to Booking</h2>
            <p className="text-sm text-ga-text-muted mb-4">
              <strong className="text-ga-text-primary">{promoteModal.contact_name}</strong> — {promoteModal.service_name} on {fmtDate(promoteModal.date)}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Total Amount (₹)</label>
                <input
                  type="number" min="0"
                  value={promoteModal.total_amount}
                  onChange={e => setPromoteModal(prev => ({ ...prev, total_amount: e.target.value }))}
                  className="ga-input w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Advance (₹)</label>
                <input
                  type="number" min="0"
                  value={promoteModal.advance_amount}
                  onChange={e => setPromoteModal(prev => ({ ...prev, advance_amount: e.target.value }))}
                  className="ga-input w-full text-sm"
                />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs text-ga-text-muted mb-1">Payment Mode</label>
              <select
                value={promoteModal.payment_mode}
                onChange={e => setPromoteModal(prev => ({ ...prev, payment_mode: e.target.value }))}
                className="ga-input w-full text-sm"
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPromoteModal(null)}
                className="ga-btn-ghost flex-1 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmPromote}
                disabled={promotingId === promoteModal.id}
                className="ga-btn-primary flex-1 text-sm"
              >
                {promotingId === promoteModal.id ? 'Creating…' : 'Create Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Waitlist;
